import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, NavigateNext, Save } from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';
import { showNotification } from '../store/notificationSlice';
import { useAppDispatch, useAppSelector } from '../store';
import { upsertLeavePolicy } from '../store/leavePoliciesSlice';
import { apiService } from '../services/api';
import type { LeavePolicy, CreateLeavePolicyPayload } from '../types/leave';

const ITEM_HEIGHT = 40;

const ACCRUAL_STRATEGIES = [
  { value: 'YEARLY', label: 'Yearly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'BI_WEEKLY', label: 'Bi-Weekly' },
  { value: 'HOURLY', label: 'Hourly' },
  { value: 'MILESTONE', label: 'Milestone' },
  { value: 'NONE', label: 'None (No Accrual)' },
];

function emptyPolicy(leaveTypeId: string): CreateLeavePolicyPayload {
  return {
    leaveTypeId,
    effectiveDate: new Date().toISOString().split('T')[0],
    accrualStrategy: 'MONTHLY',
    accrualRate: 0,
    waitingPeriodDays: 0,
    isCarryForward: false,
    maxCarryForward: 0,
    maxConsecutiveDays: 0,
    allowNegativeBalance: false,
    negativeLimit: 0,
    sandwichWeekendLimit: 0,
    sandwichHolidayLimit: 0,
    leaveYearType: 'FIXED',
    lapseMonth: 1,
    lapseDay: 1,
  };
}

function policyToForm(p: LeavePolicy): CreateLeavePolicyPayload {
  return {
    leaveTypeId: p.leaveTypeId,
    effectiveDate: p.effectiveDate,
    accrualStrategy: p.accrualStrategy === 'FIXED' ? 'YEARLY' : p.accrualStrategy,
    accrualRate: p.accrualRate,
    waitingPeriodDays: p.waitingPeriodDays,
    isCarryForward: p.isCarryForward,
    maxCarryForward: p.maxCarryForward,
    maxConsecutiveDays: p.maxConsecutiveDays,
    allowNegativeBalance: p.allowNegativeBalance,
    negativeLimit: p.negativeLimit,
    sandwichWeekendLimit: p.sandwichWeekendLimit,
    sandwichHolidayLimit: p.sandwichHolidayLimit,
    leaveYearType: p.leaveYearType ?? 'FIXED',
    lapseMonth: p.lapseMonth ?? 1,
    lapseDay: p.lapseDay ?? 1,
  };
}

const formatDate = (d: string) => {
  const [y, m, day] = d.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m, 10) - 1]} ${parseInt(day, 10)}, ${y}`;
};

const LeavePolicies: React.FC = () => {
  const dispatch = useAppDispatch();
  const leaveTypes = useAppSelector((s) => s.leaveTypes.leaveTypes);
  const allPolicies = useAppSelector((s) => s.leavePolicies.leavePolicies);
  const [selectedTypeId, setSelectedTypeId] = useState('');

  // Timeline state
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [mode, setMode] = useState<'view' | 'create'>('view');
  const [form, setForm] = useState<CreateLeavePolicyPayload>(emptyPolicy(''));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [tlScrollOffset, setTlScrollOffset] = useState(0);
  const formPanelRef = useRef<HTMLDivElement>(null);

  // Auto-select first leave type
  useEffect(() => {
    if (leaveTypes.length > 0 && !selectedTypeId) {
      setSelectedTypeId(leaveTypes[0].id);
    }
  }, [leaveTypes, selectedTypeId]);

  // Filter policies for selected type
  const policies = useMemo(
    () => allPolicies.filter((p) => p.leaveTypeId === selectedTypeId).sort((a, b) => a.effectiveDate.localeCompare(b.effectiveDate)),
    [allPolicies, selectedTypeId],
  );

  // Select latest version when policies or selected type changes
  useEffect(() => {
    if (policies.length > 0) {
      const latest = policies[policies.length - 1];
      setSelectedDate(latest.effectiveDate);
      setForm(policyToForm(latest));
      setMode('view');
    } else {
      setSelectedDate(null);
      setMode('view');
    }
  }, [selectedTypeId, policies]);

  const handleTimelineSelect = (date: string) => {
    const policy = policies.find((p) => p.effectiveDate === date);
    if (policy) {
      setSelectedDate(date);
      setForm(policyToForm(policy));
      setMode('view');
      setErrors({});
    }
  };

  const handleNewVersion = () => {
    setMode('create');
    setSelectedDate(null);
    setForm(emptyPolicy(selectedTypeId));
    setErrors({});
    formPanelRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const set = (field: keyof CreateLeavePolicyPayload, val: any) => setForm((prev) => ({ ...prev, [field]: val }));

  const handleSave = async () => {
    setSaving(true);
    setErrors({});
    try {
      let res: { data: LeavePolicy };
      if (mode === 'create') {
        res = await apiService.createLeavePolicy(form);
        dispatch(showNotification({ message: 'Policy created', severity: 'success' }));
      } else {
        res = await apiService.updateLeavePolicy(form);
        dispatch(showNotification({ message: 'Policy updated', severity: 'success' }));
      }
      dispatch(upsertLeavePolicy(res.data));
      setSelectedDate(res.data.effectiveDate);
      setForm(policyToForm(res.data));
      setMode('view');
    } catch (err: any) {
      if (err?.jsonMessage) {
        setErrors(err.jsonMessage);
      } else {
        dispatch(showNotification({ message: err?.message ?? 'Failed to save policy', severity: 'error' }));
      }
    } finally {
      setSaving(false);
    }
  };

  // Sorted newest-first for timeline
  const sortedDates = [...policies].reverse().map((p) => p.effectiveDate);

  const isEditable = mode === 'create' || selectedDate !== null;

  return (
    <DashboardLayout>
      <Box sx={{ display: 'flex', flexDirection: 'column', m: -3, minHeight: 'calc(100vh - 64px)' }}>
        <Box sx={{ px: 3, pt: 3 }}>
          <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">Leave Management</Typography>
            <Typography variant="body2" color="text.primary" fontWeight={600}>Leave Policies</Typography>
          </Breadcrumbs>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>Leave Policies</Typography>
          </Box>
        </Box>

        <>
            <Box sx={{ px: 3, mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 250 }}>
                <InputLabel>Leave Type</InputLabel>
                <Select
                  label="Leave Type"
                  value={selectedTypeId}
                  onChange={(e) => setSelectedTypeId(e.target.value)}
                >
                  {leaveTypes.map((lt) => (
                    <MenuItem key={lt.id} value={lt.id}>{lt.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{
                display: 'flex',
                mx: 3,
                mb: 3,
                height: 'calc(100vh - 280px)',
                minHeight: 300,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper',
                overflow: 'hidden',
              }}>
                {/* Left panel: timeline */}
                <Box sx={{
                  width: 170,
                  flexShrink: 0,
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  p: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', color: 'text.secondary' }}>
                      Versions
                    </Typography>
                    <Tooltip title="New Version">
                      <IconButton size="small" onClick={handleNewVersion} disabled={!selectedTypeId}>
                        <Add fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Scrollable timeline */}
                  <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
                    <Box
                      onScroll={(e) => setTlScrollOffset(e.currentTarget.scrollTop)}
                      sx={{
                        height: '100%', overflowY: 'auto', userSelect: 'none',
                        '&::-webkit-scrollbar': { width: 5 },
                        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                        '&::-webkit-scrollbar-thumb': { bgcolor: '#bdbdbd', borderRadius: 3 },
                        scrollbarWidth: 'thin',
                      }}
                    >
                      {/* Upward arrow */}
                      <Box sx={{
                        position: 'sticky', top: 0, left: 16, transform: 'translateX(-50%)',
                        width: 0, height: 0,
                        borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
                        borderBottom: '8px solid', borderBottomColor: '#e0e0e0', zIndex: 0,
                      }} />

                      {sortedDates.length === 0 && mode !== 'create' ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, px: 1 }}>
                          No policies
                        </Typography>
                      ) : (
                        sortedDates.map((d) => {
                          const isSelected = d === selectedDate && mode === 'view';
                          return (
                            <Box
                              key={d}
                              onClick={() => handleTimelineSelect(d)}
                              sx={{
                                display: 'flex', alignItems: 'center', height: ITEM_HEIGHT,
                                cursor: 'pointer',
                                '&:hover .date-label': { color: 'primary.main' },
                              }}
                            >
                              {/* Line + dot */}
                              <Box sx={{ width: 32, position: 'relative', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 2, bgcolor: '#e0e0e0', transform: 'translateX(-50%)' }} />
                                <Box sx={{
                                  width: isSelected ? 16 : 10, height: isSelected ? 16 : 10,
                                  borderRadius: '50%', bgcolor: isSelected ? 'primary.main' : '#bdbdbd',
                                  zIndex: 1, transition: 'all 0.15s',
                                  border: isSelected ? '2px solid' : 'none', borderColor: 'primary.main',
                                }} />
                              </Box>
                              <Typography
                                className="date-label"
                                variant="body2"
                                sx={{ flex: 1, fontWeight: isSelected ? 700 : 400, color: isSelected ? 'primary.main' : 'text.primary', transition: 'color 0.15s', fontSize: '0.82rem' }}
                              >
                                {formatDate(d)}
                              </Typography>
                            </Box>
                          );
                        })
                      )}

                      {/* Trailing line */}
                      {sortedDates.length > 0 && (
                        <Box sx={{ width: 32, height: 20, position: 'relative' }}>
                          <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 2, bgcolor: '#e0e0e0', transform: 'translateX(-50%)' }} />
                        </Box>
                      )}
                    </Box>

                    {/* Top fade */}
                    <Box sx={{
                      position: 'absolute', top: 0, left: 0, right: 0, height: 32, pointerEvents: 'none',
                      background: 'linear-gradient(to bottom, rgba(255,255,255,0.95), rgba(255,255,255,0))',
                      opacity: Math.min(1, tlScrollOffset / 40), transition: 'opacity 0.15s',
                    }} />
                    {/* Bottom fade */}
                    <Box sx={{
                      position: 'absolute', bottom: 0, left: 0, right: 0, height: 32, pointerEvents: 'none',
                      background: 'linear-gradient(to top, rgba(255,255,255,0.95), rgba(255,255,255,0))',
                      opacity: sortedDates.length * ITEM_HEIGHT > tlScrollOffset + 100 ? 1 : 0,
                      transition: 'opacity 0.15s',
                    }} />
                  </Box>
                </Box>

                {/* Right panel: policy form */}
                <Box ref={formPanelRef} sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  {/* Header bar */}
                  <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: '24.5px' }}>
                      <Box component="span" sx={{ color: 'primary.main' }}>
                        {mode === 'create' ? 'New Version' : 'Version'}
                      </Box>
                      {mode === 'view' && selectedDate && (
                        <>
                          {' | '}
                          {formatDate(selectedDate)}
                        </>
                      )}
                    </Typography>
                  </Box>

                  {/* Scrollable form body */}
                  <Box sx={{
                    flex: 1, overflowY: 'auto', px: 3, py: 2,
                    '&::-webkit-scrollbar': { width: 6 },
                    '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { bgcolor: '#bdbdbd', borderRadius: 3 },
                    scrollbarWidth: 'thin',
                  }}>
                    {!isEditable ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography color="text.secondary">
                          {policies.length === 0 ? 'No policies yet. Click + to create one.' : 'Select a version from the timeline.'}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 520 }}>
                        <TextField
                          label="Effective Date"
                          type="date"
                          value={form.effectiveDate}
                          onChange={(e) => set('effectiveDate', e.target.value)}
                          size="small"
                          fullWidth
                          disabled={mode === 'view'}
                          error={!!errors['effectiveDate']}
                          helperText={errors['effectiveDate']}
                          slotProps={{ inputLabel: { shrink: true } }}
                        />

                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                          <FormControl size="small" error={!!errors['accrualStrategy']}>
                            <InputLabel>Accrual Strategy</InputLabel>
                            <Select
                              label="Accrual Strategy"
                              value={form.accrualStrategy}
                              onChange={(e) => {
                                const val = e.target.value;
                                set('accrualStrategy', val);
                                if (val === 'NONE') {
                                  set('accrualRate', 0);
                                }
                              }}
                            >
                              {ACCRUAL_STRATEGIES.map((s) => (
                                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            label="Accrual Rate"
                            type="number"
                            value={form.accrualStrategy === 'NONE' ? 0 : form.accrualRate}
                            onChange={(e) => set('accrualRate', Number(e.target.value))}
                            size="small"
                            disabled={form.accrualStrategy === 'NONE'}
                            helperText={form.accrualStrategy === 'NONE' ? 'N/A for None' : undefined}
                            inputProps={{ step: 0.5, min: 0 }}
                          />
                        </Box>

                        <TextField label="Waiting Period (days)" type="number" value={form.waitingPeriodDays} onChange={(e) => set('waitingPeriodDays', Number(e.target.value))} size="small" inputProps={{ min: 0 }} />
                        <TextField label="Max Consecutive Days" type="number" value={form.maxConsecutiveDays} onChange={(e) => set('maxConsecutiveDays', Number(e.target.value))} size="small" inputProps={{ min: 0 }} />

                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                          <FormControlLabel
                            control={<Checkbox checked={form.isCarryForward} onChange={(e) => set('isCarryForward', e.target.checked)} />}
                            label="Carry Forward"
                          />
                          <TextField label="Max Carry Forward" type="number" value={form.maxCarryForward} onChange={(e) => set('maxCarryForward', Number(e.target.value))} size="small" disabled={!form.isCarryForward} inputProps={{ step: 0.5, min: 0 }} />
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                          <FormControlLabel
                            control={<Checkbox checked={form.allowNegativeBalance} onChange={(e) => set('allowNegativeBalance', e.target.checked)} />}
                            label="Allow Negative Balance"
                          />
                          <TextField label="Negative Limit" type="number" value={form.negativeLimit} onChange={(e) => set('negativeLimit', Number(e.target.value))} size="small" disabled={!form.allowNegativeBalance} inputProps={{ step: 0.5, min: 0 }} />
                        </Box>

                        <Typography variant="subtitle2" sx={{ mt: 1 }}>Sandwich Rules</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                          <TextField
                            label="Weekend Limit"
                            type="number"
                            value={form.sandwichWeekendLimit}
                            onChange={(e) => set('sandwichWeekendLimit', Number(e.target.value))}
                            size="small"
                            helperText="0=none, -1=all, N=cap"
                            inputProps={{ min: -1 }}
                          />
                          <TextField
                            label="Holiday Limit"
                            type="number"
                            value={form.sandwichHolidayLimit}
                            onChange={(e) => set('sandwichHolidayLimit', Number(e.target.value))}
                            size="small"
                            helperText="0=none, -1=all, N=cap"
                            inputProps={{ min: -1 }}
                          />
                        </Box>

                        <Typography variant="subtitle2" sx={{ mt: 1 }}>Leave Year Reset</Typography>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Leave Year Type</InputLabel>
                          <Select
                            label="Leave Year Type"
                            value={form.leaveYearType}
                            onChange={(e) => set('leaveYearType', e.target.value)}
                          >
                            <MenuItem value="FIXED">Fixed Date (same day each year for all employees)</MenuItem>
                            <MenuItem value="ANNIVERSARY">Anniversary (based on each employee's joining date)</MenuItem>
                            <MenuItem value="NEVER">Never (does not reset / no expiry)</MenuItem>
                          </Select>
                        </FormControl>

                        {form.leaveYearType === 'FIXED' && (
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <TextField
                              label="Lapse Month"
                              type="number"
                              value={form.lapseMonth ?? 1}
                              onChange={(e) => set('lapseMonth', Number(e.target.value))}
                              size="small"
                              helperText="1–12"
                              inputProps={{ min: 1, max: 12 }}
                            />
                            <TextField
                              label="Lapse Day"
                              type="number"
                              value={form.lapseDay ?? 1}
                              onChange={(e) => set('lapseDay', Number(e.target.value))}
                              size="small"
                              helperText="1–31"
                              inputProps={{ min: 1, max: 31 }}
                            />
                          </Box>
                        )}

                        <Button
                          variant="contained"
                          startIcon={saving ? <CircularProgress size={16} /> : <Save />}
                          onClick={handleSave}
                          disabled={saving}
                          sx={{ mt: 2, alignSelf: 'flex-start' }}
                        >
                          {mode === 'create' ? 'Create' : 'Save'}
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
          </>
      </Box>
    </DashboardLayout>
  );
};

export default LeavePolicies;
